import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import FlexSearch from "flexsearch";
import { useStaticQuery, graphql, Link, navigate } from "gatsby";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes, faXmark } from "@fortawesome/free-solid-svg-icons";
import { faClone } from "@fortawesome/free-regular-svg-icons";
import { modifyColor } from "../utils/helpers";
import { _ } from "../utils/i18n";
import { useLocation } from "@reach/router";
import { GlobalDispatchContext } from "../context/global-context-provider";
import { useReducer } from "react";

const Wrapper = styled.div`
  --max-width: 600px;
  --calculated-width: calc(
    var(--max-width) - 2rem + 1px
  ); /* max width minus the right margin on SearchBar */
  width: 100%;
  max-width: var(--max-width);
  display: flex;
  position: relative;
  justify-content: flex-end;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    justify-content: flex-start;
    transition: all 0.3s ease-in-out;
    margin: 0;
    transform: ${({ $searchOpen }) =>
      $searchOpen ? "translateY(0)" : "translateY(-100%)"};
    position: fixed;
    top: 0;
    padding-inline: 20px;
    background-color: var(--color-secondary);
    z-index: 999;
    max-width: unset;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    padding: 0 10px;
  }
`;
const SearchBar = styled.div`
  display: flex;
  flex-flow: row nowrap;
  margin-right: 2rem;
  width: 100%;
  z-index: 9999;
  background-color: ${({ theme }) =>
    theme.colors.headerSearch || `var(--color-primary)`};
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    margin-right: 0;
  }
`;
const SearchInput = styled.input`
  font-size: 16px !important;
  padding: 15px 15px;
  box-shadow: none;
  width: 100%;
  border: 1px solid
    ${({ theme }) => theme.colors.headerSearch || `var(--color-primary)`};
  background-color: ${({ theme }) =>
    theme.colors.headerSearch || `var(--color-primary)`};
  &:focus {
    outline: none;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 50px;
    width: 100%;
  }
`;
const SearchButton = styled.button`
  background-color: ${({ theme }) =>
    theme.colors.headerSearch || `var(--color-primary)`};
  padding: 15px;
  border: none;
  border-right: 1px solid
    ${({ theme }) => theme.colors.headerSearch || `var(--color-primary)`};
  cursor: pointer;
  transition: all 0.5s ease;
  @media (hover: hover) {
    &:hover {
      background-color: ${({ theme }) =>
        modifyColor(theme.colors.headerSearch || `var(--color-primary)`, -40)};
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 50px;
  }
`;
const SearchIcon = styled(FontAwesomeIcon)`
  color: var(--color-body-text);
`;
const SearchResults = styled.div`
  position: absolute;
  top: 49px;
  right: 32px;
  width: 100%;
  max-width: var(--calculated-width);
  background-color: #fff;
  border: 1px solid var(--color-primary);
  max-height: 550px;
  overflow-y: scroll;
  z-index: 9999;
  visibility: ${({ $searchOpen }) => ($searchOpen ? "visible" : "hidden")};
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    position: relative;
    width: 100%;
    top: 0;
    right: 0;
    background-color: transparent;
    border: none;
  }
`;

const Result = styled(Link)`
  display: flex;
  padding: 15px 20px;
  border-bottom: 1px solid var(--color-primary);
  text-decoration: none;
  transition: all 0.5 ease;
  color: var(--color-primary);
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background-color: #efefef;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    font-size: 1.3rem;
  }
`;
const Content = styled.div`
  font-size: 0.9rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    font-size: 1rem;
  }
`;

const MobileClose = styled.div`
  font-size: 1.5rem;
  color: var(--color-primary);
  padding: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  background-color: rgba(0, 0, 0, 0.8);
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 99;
  visibility: ${({ $searchOpen }) => ($searchOpen ? "visible" : "hidden")};
  opacity: ${({ $searchOpen }) => ($searchOpen ? 1 : 0)};
  transition: all 0.3s ease-in-out;
`;

const SearchItemRow = styled.div`
  display: flex;
  align-items: center;
`;

const SearchItemIcon = styled(FontAwesomeIcon)`
  font-size: 24px;
  margin-right: 16px;
`;

const ResultTitle = styled.h4`
  font-weight: 600;
  margin: 32px 0 0 16px;
`;

const Search = ({ staticSearchData, searchOpen, onOpen, onClose }) => {
  const { localSearchPages } =
    staticSearchData ||
    useStaticQuery(graphql`
      query SearchsearchQuery {
        localSearchPages {
          publicIndexURL
          publicStoreURL
        }
      }
    `);

  const [searchIndex, setSearchIndex] = useState(null);
  const [searchStore, setSearchStore] = useState(null);
  const { pathname, search } = useLocation();
  const [shouldLoadSearch, loadSearch] = useReducer(
    () => true,
    new URLSearchParams(search).get("søkeord") ? true : false
  );
  const [searchQuery, setSearchQuery] = useState(
    new URLSearchParams(search).get("søkeord") ?? ""
  );
  const [rawResults, setRawResults] = useState(null);
  const [results, setResults] = useState(null);
  const [flexSearch, setFlexSearch] = useState(null);
  const [privateOrBusiness, setPrivateOrBusiness] = useState(
    typeof window !== "undefined" && localStorage.getItem("private-business")
  );
  const dispatch = useContext(GlobalDispatchContext);

  useEffect(() => {
    // Load search index
    if (shouldLoadSearch && !searchIndex) {
      fetch(localSearchPages?.publicIndexURL)
        .then((response) => response.text())
        .then((index) => setSearchIndex(index));
    }
  }, [shouldLoadSearch, searchIndex]);

  useEffect(() => {
    // Load search store
    if (shouldLoadSearch && !searchStore) {
      fetch(localSearchPages?.publicStoreURL)
        .then((response) => response.json())
        .then((store) => setSearchStore(store));
    }
  }, [shouldLoadSearch, searchStore]);

  useEffect(() => {
    // Create search object
    if (!flexSearch && searchIndex) {
      const newFlexSearch = FlexSearch.create();
      newFlexSearch.import(searchIndex);
      setFlexSearch(newFlexSearch);
    }
  }, [flexSearch, searchIndex]);

  useEffect(() => {
    // Do the actual search
    if (flexSearch && searchStore) {
      setRawResults(
        flexSearch.search(searchQuery).map((id) => searchStore[id])
      );
    }
    dispatch({ type: "UPDATE_SEARCH_WORD", value: searchQuery });

    const params = new URLSearchParams(search);
    if (params.has("søkeord")) {
      params.set("søkeord", searchQuery);
      navigate(`${pathname}?${params.toString()}`, { replace: true });
    }
  }, [searchQuery, flexSearch, searchStore]);

  useEffect(() => {
    if (!rawResults) {
      return;
    }
    // Process the search results returned by FlexSearch, highlight matches etc
    const sentenceRegex = new RegExp(
      "[^.,]*(" +
        searchQuery
          ?.split(/\s+/)
          .filter((i) => i?.length)
          .join("|") +
        ")[^.,]*",
      "gi"
    );

    const regex = new RegExp(
      searchQuery
        ?.split(/\s+/)
        .filter((i) => i?.length)
        .join("|"),
      "gi"
    );

    if (searchQuery.length > 1) {
      const newResults = rawResults
        ?.map((result) => {
          const matches = [
            ...(result.content?.matchAll(sentenceRegex) || null),
          ];
          return {
            title: result.title,
            id: result.id,
            uri: result.uri,
            postType: result.postType,
            artNr: result.artNr,
            isCategory: result.isCategory,
            node: result?.node,
            matches: matches.map(([sentence]) =>
              sentence.replace(regex, (match) => `<strong>${match}</strong>`)
            ),
          };
        })
        .filter((result) => {
          if (
            result.postType === "SimpleProduct" ||
            result.postType === "VariableProduct"
          ) {
            const isBusinessCategory =
              result?.node?.productCategories?.nodes?.some(
                (category) => category.name === "Produkter - Bedrift"
              );
            return privateOrBusiness === "business"
              ? isBusinessCategory
              : !isBusinessCategory;
          } else {
            return true;
          }
        });
      setResults(newResults);

      dispatch({
        type: "UPDATE_SEARCH_RESULTS",
        value: newResults
          .map((result) => result.node)
          .sort((a, b) => (a.menuOrder > b.menuOrder ? 1 : -1)),
      });
    } else {
      setResults([]);
      dispatch({
        type: "UPDATE_SEARCH_RESULTS",
        value: [],
      });
    }
  }, [rawResults, privateOrBusiness]);

  const doSearch = (e) => {
    let onlyLetters = /^[-A-Za-z0-9\u00C0-\u00ff ]+$/.test(e);
    if (onlyLetters || e === "") {
      setSearchQuery(e);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (pathname.indexOf(encodeURI("/søkeresultater")) > -1) {
        navigate(`?søkeord=${searchQuery}`);
      } else {
        if (privateOrBusiness === "business") {
          navigate(`/produkter-bedrift/søkeresultater?søkeord=${searchQuery}`);
        } else {
          navigate(`/nettbutikk/søkeresultater?søkeord=${searchQuery}`);
        }
      }
      onClose();
    }
  };

  const onClearIconClick = () => {
    setSearchQuery("");
  };

  const translatePostType = (postType) => {
    if (postType === "SimpleProduct" || postType === "VariableProduct") {
      return "Produkt";
    } else if (postType === "Page") {
      return "Side";
    } else if (postType === "Post") {
      return "Innlegg";
    } else {
      return "Produktkategori";
    }
  };

  const checkPrivateOrBusiness = () => {
    setPrivateOrBusiness(localStorage.getItem("private-business"));
  };

  useEffect(() => {
    window.addEventListener("storage", checkPrivateOrBusiness);
    return () => removeEventListener("storage", checkPrivateOrBusiness);
  }, []);

  return (
    <>
      <Wrapper $searchOpen={searchOpen}>
        <SearchBar onClickCapture={onOpen}>
          <SearchButton>
            <SearchIcon icon={faSearch} size="lg" />
          </SearchButton>
          <SearchInput
            type="text"
            placeholder={_("Søk på nettsiden")}
            value={searchQuery}
            onChange={(event) => doSearch(event.target.value)}
            onFocus={() => {
              loadSearch();
            }}
            onKeyDown={handleKeyDown}
          />
          <SearchButton onClick={onClearIconClick}>
            <SearchIcon icon={faXmark} size="lg" />
          </SearchButton>
          <MobileClose onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </MobileClose>
        </SearchBar>
        {results?.length > 0 && (
          <SearchResults $searchOpen={searchOpen}>
            <SearchItemRow>
              <Content>
                <ResultTitle>Søkeforslag:</ResultTitle>
              </Content>
            </SearchItemRow>
            {results.map((result) => (
              <Result
                key={result.id}
                style={{ flexDirection: "column" }}
                to={result.uri}
              >
                <SearchItemRow>
                  <SearchItemIcon
                    icon={result.isCategory ? faClone : faSearch}
                  />
                  <div>
                    <h3 style={{ textTransform: "uppercase" }}>
                      {translatePostType(result.postType)}
                    </h3>
                    <h4 style={{ fontWeight: result.isCategory ? 400 : 600 }}>
                      {result.title}
                    </h4>
                    {result.artNr && <Content>Art.Nr: {result.artNr}</Content>}
                    {result.matches.map((match, i) => (
                      <Content
                        key={i}
                        dangerouslySetInnerHTML={{ __html: match }}
                      ></Content>
                    ))}
                  </div>
                </SearchItemRow>
              </Result>
            ))}
          </SearchResults>
        )}
      </Wrapper>
      <Backdrop $searchOpen={searchOpen} onClick={onClose} />
    </>
  );
};

Search.propTypes = {
  searchOpen: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  staticSearchData: PropTypes.object,
};

export default Search;
